import { useCallback, useState } from "react";
import { z } from "zod";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { FormSubmitButton } from "~/components/form-fields/FormSubmitButton";
import { FormErrorSummary } from "~/components/form-fields/FormErrorSummary";
import { ValidatedInput } from "~/components/form-fields/ValidatedInput";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { AppleIcon, GoogleIcon, MicrosoftIcon, PasskeyIcon } from "~/components/ui/icons";
import { claimImportedAccount } from "~/features/auth/auth.mutations";
import { getAccountLockStatus } from "~/features/security/security.queries";
import { passkeyAuth } from "~/lib/auth-passkey-client";
import { auth } from "~/lib/auth-client";
import { usePasskeySupport } from "~/lib/auth/use-passkey-support";
import { useAppForm } from "~/lib/hooks/useAppForm";
import { useFocusWhen } from "~/lib/hooks/use-focus-when";
import { useHydrated } from "~/lib/hooks/use-hydrated";
import { getBrand } from "~/tenant";
import {
  getOAuthErrorMessage,
  settleAsyncResult,
  settleAuthClientResult,
} from "../auth-client-utils";
import { getFriendlyAuthErrorMessage } from "../auth-error-messages";
import {
  buildForgotPasswordHref,
  buildSignupHref,
  decodePostAuthIntent,
  normalizeAuthRedirectPath,
  resolvePostAuthRedirectPath,
} from "../auth-routing";
import {
  checkPasskeysByEmail,
  lookupAccountRecoveryByEmail,
  type SocialAuthProvider,
} from "../auth.queries";
import { loginFormSchema, signupFormFields } from "../auth.schemas";
import { useAuthSuccessHandler } from "../hooks/use-auth-success-handler";
import { AuthDivider } from "./auth-divider";
import { AuthPageFooter } from "./auth-page-footer";
import { AuthPageHeader } from "./auth-page-header";
import { LoginPasskeyStep } from "./login-passkey-step";
import { LoginTwoFactorStep } from "./login-two-factor-step";

type LoginFormProps = {
  redirectPath?: string | undefined;
  postAuthIntent?: string | undefined;
  oauthRedirectPath?: string | undefined;
  socialProviders?: SocialAuthProvider[];
  loginReason?: "session_expired" | "unauthorized" | undefined;
  onBeforeOAuth?: () => void;
  onAuthenticated?: () => void;
};

type ImportedAccountClaimCandidate = {
  email: string;
  name: string;
};

export default function LoginForm(props?: LoginFormProps) {
  const { i18n } = useLingui();
  const brand = getBrand();
  const onAuthenticated = props?.onAuthenticated;

  const postAuthIntent = decodePostAuthIntent(props?.postAuthIntent);
  const safeRedirectPath = resolvePostAuthRedirectPath(
    props?.redirectPath,
    "/dashboard",
    props?.postAuthIntent,
  );
  const requestedRedirectPath =
    normalizeAuthRedirectPath(props?.redirectPath) ?? postAuthIntent?.returnTo;
  const safeOauthRedirectPath =
    normalizeAuthRedirectPath(props?.oauthRedirectPath) ?? safeRedirectPath;
  const signupHref = buildSignupHref({
    redirectPath: requestedRedirectPath,
    intent: postAuthIntent,
  });
  const loginReasonMessage =
    props?.loginReason === "session_expired"
      ? i18n._(msg`Your session has expired. Please sign in again.`)
      : props?.loginReason === "unauthorized"
        ? i18n._(msg`Please sign in to continue.`)
        : null;

  const isHydrated = useHydrated();
  const passkeySupported = usePasskeySupport();
  const handleAuthSuccess = useAuthSuccessHandler({
    onAuthenticated,
    redirectTo: safeRedirectPath,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState<"totp" | "backup">("totp");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorError, setTwoFactorError] = useState("");
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [loginStep, setLoginStep] = useState<"email" | "passkey-prompt" | "credentials">(
    "email",
  );
  const [userHasPasskeys, setUserHasPasskeys] = useState(false);
  const [checkingPasskeys, setCheckingPasskeys] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState("");
  const [claimCandidate, setClaimCandidate] =
    useState<ImportedAccountClaimCandidate | null>(null);

  const emailInputRef = useFocusWhen<HTMLInputElement>({
    enabled: loginStep === "email" && !twoFactorRequired,
    delayMs: 25,
  });
  const passwordInputRef = useFocusWhen<HTMLInputElement>({
    enabled: loginStep === "credentials" && !twoFactorRequired,
    delayMs: 25,
  });
  const passkeyButtonRef = useFocusWhen<HTMLButtonElement>({
    enabled: loginStep === "passkey-prompt" && !twoFactorRequired,
    delayMs: 25,
  });
  const twoFactorCodeRef = useFocusWhen<HTMLInputElement>({
    enabled: twoFactorRequired,
    delayMs: 25,
  });

  const socialProviders = props?.socialProviders ?? ["google"];
  const providerConfig: Record<
    SocialAuthProvider,
    { label: string; Icon: typeof GoogleIcon }
  > = {
    google: { label: i18n._(msg`Google`), Icon: GoogleIcon },
    microsoft: { label: i18n._(msg`Microsoft`), Icon: MicrosoftIcon },
    apple: { label: i18n._(msg`Apple`), Icon: AppleIcon },
  };

  const resetToEmailStep = () => {
    setLoginStep("email");
    setCheckedEmail("");
    setClaimCandidate(null);
    setUserHasPasskeys(false);
  };

  const handleEmailContinue = useCallback(
    async (email: string) => {
      setClaimCandidate(null);
      if (!email) {
        setLoginStep("credentials");
        return;
      }

      const emailValidation = z.email().safeParse(email);
      if (!emailValidation.success) {
        setLoginStep("credentials");
        return;
      }

      const normalizedEmail = emailValidation.data.trim().toLowerCase();

      if (checkedEmail === normalizedEmail) {
        if (claimCandidate?.email === normalizedEmail) {
          setLoginStep("credentials");
          return;
        }

        setLoginStep(userHasPasskeys ? "passkey-prompt" : "credentials");
        return;
      }

      setCheckingPasskeys(true);
      setErrorMessage("");

      const [recoveryLookup, passkeyLookup] = await Promise.all([
        settleAsyncResult(
          () =>
            lookupAccountRecoveryByEmail({
              data: {
                email: normalizedEmail,
              },
            }),
          i18n._(msg`Unable to check account recovery right now.`),
        ),
        passkeySupported
          ? settleAsyncResult(
              () => checkPasskeysByEmail({ data: { email: normalizedEmail } }),
              i18n._(msg`Unable to check passkeys right now.`),
            )
          : Promise.resolve({
              data: {
                hasPasskeys: false,
              },
              errorMessage: null,
            }),
      ]);

      if (
        recoveryLookup.data?.match === "user" &&
        recoveryLookup.data.isTopscoreImported &&
        !recoveryLookup.data.hasPasswordAccount
      ) {
        setCheckedEmail(normalizedEmail);
        setUserHasPasskeys(false);
        setClaimCandidate({
          email: normalizedEmail,
          name: recoveryLookup.data.name,
        });
        setLoginStep("credentials");
        setCheckingPasskeys(false);
        return;
      }

      if (!passkeyLookup.data) {
        setLoginStep("credentials");
        setCheckingPasskeys(false);
        return;
      }

      setCheckedEmail(normalizedEmail);
      setUserHasPasskeys(passkeyLookup.data.hasPasskeys);
      setLoginStep(passkeyLookup.data.hasPasskeys ? "passkey-prompt" : "credentials");
      setCheckingPasskeys(false);
    },
    [checkedEmail, claimCandidate?.email, i18n, passkeySupported, userHasPasskeys],
  );

  const handlePasskeySignIn = useCallback(async () => {
    if (!passkeySupported) {
      setErrorMessage(i18n._(msg`Passkeys are not supported on this device.`));
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    const { errorMessage: passkeyError } = await settleAuthClientResult(
      () => passkeyAuth.signInWithPasskey({}),
      i18n._(msg`Passkey login failed`),
    );

    if (passkeyError) {
      setErrorMessage(passkeyError);
      setIsLoading(false);
      return;
    }

    await handleAuthSuccess();
    setIsLoading(false);
  }, [handleAuthSuccess, i18n, passkeySupported]);

  const handleEmailSignInResult = useCallback(
    async (result: Awaited<ReturnType<typeof auth.signIn.email>> | null) => {
      if (
        result?.data &&
        "twoFactorRedirect" in result.data &&
        result.data.twoFactorRedirect
      ) {
        setTwoFactorRequired(true);
        return;
      }

      if (result?.data && "user" in result.data && result.data.user?.id) {
        const { data: lock } = await settleAsyncResult(
          () => getAccountLockStatus({ data: { userId: result.data.user.id } }),
          i18n._(msg`Unable to verify account access.`),
        );

        if (lock) {
          await auth.signOut();
          setErrorMessage(
            i18n._(msg`Account locked. Contact an administrator for access.`),
          );
          return;
        }
      }

      await handleAuthSuccess();
    },
    [handleAuthSuccess, i18n],
  );

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      setIsLoading(true);
      setErrorMessage("");
      const normalizedEmail = value.email.trim().toLowerCase();

      if (claimCandidate?.email === normalizedEmail) {
        const passwordError = signupFormFields.password({ value: value.password });
        if (passwordError) {
          setErrorMessage(passwordError);
          setIsLoading(false);
          return;
        }

        const { errorMessage: claimError } = await settleAsyncResult(
          () =>
            claimImportedAccount({
              data: {
                email: normalizedEmail,
                newPassword: value.password,
              },
            }),
          i18n._(msg`Unable to claim this imported account right now.`),
        );

        if (claimError) {
          setErrorMessage(claimError);
          setIsLoading(false);
          return;
        }

        const { errorMessage: claimedLoginError, result: claimedLoginResult } =
          await settleAuthClientResult(
            () =>
              auth.signIn.email({
                email: normalizedEmail,
                password: value.password,
              }),
            i18n._(msg`Unable to sign in with your new password.`),
          );

        if (claimedLoginError) {
          setErrorMessage(claimedLoginError);
          setIsLoading(false);
          return;
        }

        await handleEmailSignInResult(claimedLoginResult);
        setIsLoading(false);
        return;
      }

      const { errorMessage: loginError, result } = await settleAuthClientResult(
        () =>
          auth.signIn.email({
            email: normalizedEmail,
            password: value.password,
          }),
        i18n._(msg`Invalid email or password`),
      );

      if (loginError) {
        const normalizedError = loginError.trim().toLowerCase();
        if (normalizedError.includes("credential account not found")) {
          const { data: recovery } = await settleAsyncResult(
            () =>
              lookupAccountRecoveryByEmail({
                data: {
                  email: normalizedEmail,
                },
              }),
            i18n._(msg`Unable to check account recovery right now.`),
          );

          if (
            recovery?.match === "user" &&
            recovery.isTopscoreImported &&
            !recovery.hasPasswordAccount
          ) {
            setCheckedEmail(normalizedEmail);
            setUserHasPasskeys(false);
            setClaimCandidate({
              email: normalizedEmail,
              name: recovery.name,
            });
            setLoginStep("credentials");
            setIsLoading(false);
            return;
          }
        }

        setErrorMessage(
          getFriendlyAuthErrorMessage(
            loginError,
            i18n._(msg`Invalid email or password`),
            "login",
          ),
        );
        form.reset({
          email: value.email,
          password: "",
        });
        setIsLoading(false);
        return;
      }

      await handleEmailSignInResult(result);
      setIsLoading(false);
    },
  });

  if (twoFactorRequired) {
    return (
      <LoginTwoFactorStep
        codeInputRef={twoFactorCodeRef}
        errorMessage={twoFactorError}
        isLoading={twoFactorLoading}
        method={twoFactorMethod}
        onBack={() => {
          setTwoFactorRequired(false);
          setTwoFactorMethod("totp");
          setTwoFactorCode("");
          setTwoFactorError("");
        }}
        onCodeChange={setTwoFactorCode}
        onMethodChange={setTwoFactorMethod}
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          setTwoFactorLoading(true);
          setTwoFactorError("");

          const { errorMessage: twoFactorSubmitError } = await settleAuthClientResult(
            () =>
              twoFactorMethod === "backup"
                ? auth.twoFactor.verifyBackupCode({ code: twoFactorCode })
                : auth.twoFactor.verifyTotp({ code: twoFactorCode }),
            i18n._(msg`Invalid authentication code`),
          );

          if (twoFactorSubmitError) {
            setTwoFactorError(twoFactorSubmitError);
            setTwoFactorLoading(false);
            return;
          }

          await handleAuthSuccess();
          setTwoFactorLoading(false);
        }}
        value={twoFactorCode}
      />
    );
  }

  if (loginStep === "passkey-prompt") {
    return (
      <LoginPasskeyStep
        email={form.state.values.email}
        errorMessage={errorMessage}
        isHydrated={isHydrated}
        isLoading={isLoading}
        loginReasonMessage={loginReasonMessage}
        onUseDifferentEmail={resetToEmailStep}
        onUsePasskey={handlePasskeySignIn}
        onUsePassword={() => setLoginStep("credentials")}
        passkeyButtonRef={passkeyButtonRef}
        signupHref={signupHref}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <form
        data-testid="login-form"
        data-hydrated={isHydrated ? "true" : "false"}
        data-login-step={loginStep}
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (loginStep === "email") {
            await handleEmailContinue(form.state.values.email);
            return;
          }

          await form.handleSubmit();
        }}
      >
        <div className="flex flex-col gap-6">
          <AuthPageHeader
            eyebrow={i18n._(msg`${brand.name}`)}
            title={i18n._(msg`Sign in`)}
            description={i18n._(msg`Enter your email to continue.`)}
          />

          {loginReasonMessage ? (
            <Alert>
              <AlertDescription>{loginReasonMessage}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-5">
            {loginStep === "credentials" ? (
              <div className="space-y-2">
                <label className="text-sm font-medium">{i18n._(msg`Email`)}</label>
                <div className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2">
                  <span className="text-sm">{form.state.values.email}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-2 py-1 text-xs"
                    onClick={resetToEmailStep}
                  >
                    {i18n._(msg`Change`)}
                  </Button>
                </div>
              </div>
            ) : (
              <form.AppField
                name="email"
                validators={{
                  onChange: ({ value }) => {
                    try {
                      loginFormSchema.shape.email.parse(value);
                      return undefined;
                    } catch (error) {
                      if (error instanceof z.ZodError) {
                        return (
                          error.issues[0]?.message || i18n._(msg`Invalid email address`)
                        );
                      }
                      return i18n._(msg`Invalid email address`);
                    }
                  },
                }}
              >
                {(field) => (
                  <ValidatedInput
                    field={field}
                    inputRef={emailInputRef}
                    label={i18n._(msg`Email`)}
                    type="email"
                    placeholder={i18n._(msg`hello@example.com`)}
                    autoComplete="username webauthn"
                    data-testid="login-email"
                  />
                )}
              </form.AppField>
            )}

            {loginStep === "credentials" ? (
              <>
                {claimCandidate ? (
                  <Alert>
                    <AlertTitle>
                      {i18n._(msg`Set a password for your existing account`)}
                    </AlertTitle>
                    <AlertDescription>
                      {i18n._(
                        msg`We found your ${brand.name} profile for ${claimCandidate.name}. Create a password to finish setting up access.`,
                      )}
                    </AlertDescription>
                  </Alert>
                ) : null}

                <form.AppField
                  name="password"
                  validators={{
                    onChange: ({ value }) => {
                      if (claimCandidate) {
                        return signupFormFields.password({ value });
                      }

                      try {
                        loginFormSchema.shape.password.parse(value);
                        return undefined;
                      } catch (error) {
                        if (error instanceof z.ZodError) {
                          return (
                            error.issues[0]?.message || i18n._(msg`Password is required`)
                          );
                        }
                        return i18n._(msg`Password is required`);
                      }
                    },
                  }}
                >
                  {(field) => (
                    <ValidatedInput
                      field={field}
                      inputRef={passwordInputRef}
                      label={
                        claimCandidate
                          ? i18n._(msg`Create password`)
                          : i18n._(msg`Password`)
                      }
                      type="password"
                      placeholder={
                        claimCandidate
                          ? i18n._(msg`Create password`)
                          : i18n._(msg`Password`)
                      }
                      autoComplete={
                        claimCandidate ? "new-password" : "current-password webauthn"
                      }
                      data-testid="login-password"
                    />
                  )}
                </form.AppField>

                {claimCandidate ? null : (
                  <div className="text-right text-sm">
                    <a
                      href={buildForgotPasswordHref({
                        email: form.state.values.email,
                        redirectPath: requestedRedirectPath,
                        intent: postAuthIntent,
                      })}
                      className="underline underline-offset-4"
                    >
                      {i18n._(msg`Forgot password?`)}
                    </a>
                  </div>
                )}
              </>
            ) : null}

            <FormSubmitButton
              isSubmitting={form.state.isSubmitting || isLoading || checkingPasskeys}
              className="mt-2 w-full"
              size="lg"
              loadingText={
                checkingPasskeys
                  ? i18n._(msg`Checking...`)
                  : loginStep === "email"
                    ? i18n._(msg`Continuing...`)
                    : claimCandidate
                      ? i18n._(msg`Setting password...`)
                      : i18n._(msg`Logging in...`)
              }
            >
              {loginStep === "email"
                ? i18n._(msg`Continue`)
                : claimCandidate
                  ? i18n._(msg`Set password and continue`)
                  : i18n._(msg`Login`)}
            </FormSubmitButton>
          </div>

          {errorMessage ? <FormErrorSummary errors={[errorMessage]} /> : null}

          {loginStep === "email" ? (
            <>
              {socialProviders.length > 0 || passkeySupported ? <AuthDivider /> : null}

              <div className="space-y-2">
                {socialProviders.map((providerId) => {
                  const config = providerConfig[providerId];
                  if (!config) {
                    return null;
                  }

                  const { Icon, label } = config;
                  return (
                    <Button
                      key={providerId}
                      variant="outline"
                      className="w-full"
                      type="button"
                      disabled={isLoading || form.state.isSubmitting}
                      onClick={() => {
                        props?.onBeforeOAuth?.();

                        return auth.signInWithOAuth(
                          {
                            provider: providerId,
                            callbackURL: safeOauthRedirectPath,
                          },
                          {
                            onRequest: () => {
                              setIsLoading(true);
                              setErrorMessage("");
                            },
                            onError: (context) => {
                              setIsLoading(false);
                              setErrorMessage(
                                getOAuthErrorMessage(
                                  context,
                                  i18n._(msg`OAuth login failed`),
                                ),
                              );
                            },
                          },
                        );
                      }}
                    >
                      <Icon />
                      {i18n._(msg`Sign in with ${label}`)}
                    </Button>
                  );
                })}

                {passkeySupported ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                    disabled={isLoading || form.state.isSubmitting || !isHydrated}
                    onClick={handlePasskeySignIn}
                  >
                    <PasskeyIcon className="mr-2 h-4 w-4" />
                    {i18n._(msg`Sign in with a passkey`)}
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}

          {loginStep === "credentials" &&
          userHasPasskeys &&
          passkeySupported &&
          !claimCandidate ? (
            <Button
              variant="outline"
              className="w-full"
              type="button"
              disabled={isLoading || form.state.isSubmitting}
              onClick={handlePasskeySignIn}
            >
              <PasskeyIcon className="mr-2 h-4 w-4" />
              {i18n._(msg`Use passkey instead`)}
            </Button>
          ) : null}
        </div>
      </form>

      <AuthPageFooter
        prompt={i18n._(msg`Don't have an account?`)}
        to={signupHref}
        label={i18n._(msg`Sign up`)}
      />
    </div>
  );
}
